import React, { useState, useContext, useEffect, useRef } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';
import ModalWrapper from './ModalWrapper';
import TextField from 'Components/DesignSystem/TextField/TextField';
import { CloseIconRounded } from 'assests/GmsIcons';
import { AppContext } from 'Context/AppContext';
import { createAttributeValues } from 'api/DataReview';

interface CreateAttributeValueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (values: string[]) => void;
  /** Product name for the info bar. Only required when showInfoBar is true. */
  productName?: string;
  /** Category display name for the info bar. Only required when showInfoBar is true. */
  category?: string;
  attributeName: string;
  csKey: string;
  /**
   * Show the "Product Name / Category / Attribute" info bar at the top of the
   * modal. Defaults to true for the Create Product flow; Mapping tabs pass
   * false so the modal reads as a stand-alone "Create Attribute Value" form.
   */
  showInfoBar?: boolean;
}

/**
 * Modal for creating new attribute values
 * Supports Enter key and comma-separated value creation
 */
const CreateAttributeValueModal = ({
  isOpen,
  onClose,
  onAdd,
  productName = '',
  category = '',
  attributeName,
  csKey,
  showInfoBar = true,
}: CreateAttributeValueModalProps) => {
  const appData = useContext(AppContext);
  const [inputValue, setInputValue] = useState('');
  const [newValues, setNewValues] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The parent (AttributeValueMappingTable) sets `createForRow = null` from
  // inside `onAdd`, which conditionally unmounts this modal on the same
  // tick. The remaining `setNewValues([])` / `setInputValue('')` /
  // `setIsSubmitting(false)` calls inside the async submit handler would
  // then run on an unmounted component (React logs:
  // "Can't perform a React state update on an unmounted component").
  // Track mount status with a ref + cleanup so the async submit handler
  // can guard its post-await setStates.
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addValues();
    }
  };

  const addValues = () => {
    // Treat the whole trimmed input as ONE value — commas are part of the
    // legitimate value (e.g. "1,250 rpm", "Red, large") and users add more
    // values by pressing Enter between entries rather than comma-separating.
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    setNewValues((prev) => (prev.includes(trimmed) ? prev : [...prev, trimmed]));
    setInputValue('');
  };

  const handleRemoveValue = (indexToRemove: number) => {
    setNewValues((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  const handleAdd = async () => {
    // Flush any text typed but not yet confirmed with Enter — also as a
    // single value (commas inside belong to the value, not separators).
    let valuesToSubmit = newValues;
    const pending = inputValue.trim();
    if (pending) {
      valuesToSubmit = newValues.includes(pending) ? newValues : [...newValues, pending];
      setNewValues(valuesToSubmit);
      setInputValue('');
    }
    if (valuesToSubmit.length === 0) return;

    const industryId = appData?.selectedIndustry?.id;
    if (!industryId) {
      toast.error('Industry not selected');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await createAttributeValues(csKey, {
        industry_id: industryId,
        attribute: attributeName,
        attribute_values: valuesToSubmit,
      });

      if (response.done) {
        toast.success(response.message || `Created ${response.created_values.length} value(s) for attribute '${attributeName}'`);
        // Reset local state BEFORE onAdd, since onAdd in the parent
        // typically closes the modal (setCreateForRow(null)) and any
        // setState after that would target an unmounted component.
        if (isMountedRef.current) {
          setNewValues([]);
          setInputValue('');
        }
        onAdd(valuesToSubmit);
      } else {
        toast.error(response.message || 'Failed to create attribute values');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to create attribute values');
    } finally {
      // The success-path onAdd above may have unmounted us; guard the
      // final spinner reset so we don't log a setState-after-unmount.
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

  const handleClose = () => {
    setNewValues([]);
    setInputValue('');
    onClose();
  };

  const renderInfoBar = () => (
    <div className="tw-rounded tw-mb-4 tw-flex tw-gap-4">
      <div className="tw-flex-1 tw-flex tw-items-center tw-gap-1">
        <Typography variant="caption-metadata" className="tw-text-ink-secondary tw-shrink-0">
          Product Name:
        </Typography>
        <Typography variant="body-primary" className="tw-text-ink tw-truncate">
          {productName}
        </Typography>
      </div>
      <div className="tw-flex-1 tw-flex tw-items-center tw-gap-1">
        <Typography variant="caption-metadata" className="tw-text-ink-secondary tw-shrink-0">
          Category:
        </Typography>
        <Typography variant="body-primary" className="tw-text-ink tw-truncate">
          {category}
        </Typography>
      </div>
      <div className="tw-flex-1 tw-flex tw-items-center tw-gap-1">
        <Typography variant="caption-metadata" className="tw-text-ink-secondary tw-shrink-0">
          Attribute:
        </Typography>
        <Typography variant="body-primary" className="tw-text-ink tw-truncate">
          {attributeName}
        </Typography>
      </div>
    </div>
  );

  const renderValueInput = () => (
    <div className="tw-mb-6">
      {/* DS TextField (2026-09-05) — the raw <input> set no placeholder ink, so
          the browser default rendered near-invisible on the dark ground. The
          field owns label, chrome, focus ring and placeholder ink now; TextField
          COMPOSES onKeyDown, so the Enter-to-add handler still fires. */}
      <TextField
        label="Enter Attribute Value Name"
        value={inputValue}
        onChange={(value) => setInputValue(value)}
        onKeyDown={handleKeyDown}
        placeholder="Type a value and press Enter to add — commas are kept as part of the value"
        data-testid="modals-createattributevaluemodal-input-1"
      />
    </div>
  );

  const renderValuesList = () => (
    <div className="tw-mb-6">
      <div className="tw-bg-surface-sunken tw-p-4 tw-rounded tw-min-h-[80px]">
        <Typography variant="sub-title-label" className="tw-text-ink-secondary">
          New Attribute Values
        </Typography>
        {newValues.length > 0 ? (
          <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mt-3">
            {newValues.map((value, index) => (
              <div
                key={index}
                className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-surface tw-border tw-border-accent tw-text-accent tw-px-3 tw-py-1 tw-rounded"
              >
                <Typography variant="body-primary">{value}</Typography>
                <button data-testid={`modals-createattributevaluemodal-button-2-${index}`}
                  type="button"
                  onClick={() => handleRemoveValue(index)}
                  className="tw-p-0 hover:tw-text-tone-danger-ink tw-transition-colors"
                  aria-label="Remove value"
                >
                  <CloseIconRounded />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <Typography variant="body-primary" className="tw-text-ink-secondary">
            No values added yet
          </Typography>
        )}
      </div>
    </div>
  );

  const renderFooter = () => (
    <div className="tw-flex tw-justify-end tw-gap-2">
      <Button variant="secondary" onClick={handleClose} data-testid="catalog-create-attribute-value-cancel-btn">
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={handleAdd}
        disabled={newValues.length === 0 || isSubmitting}
      >
        {isSubmitting ? 'Saving...' : 'Add Values'}
      </Button>
    </div>
  );

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={handleClose}
      title="Create Attribute Value"
      footer={renderFooter()}
      width="normal"
      showCloseButton={false}
      backButton
    >
      <div>
        {showInfoBar ? (
          renderInfoBar()
        ) : (
          // Compact attribute-name line for the Mapping / Data Review edit
          // flows that hide the full Product/Category info bar but still
          // need the reviewer to see which parent attribute the new values
          // will be attached to.
          attributeName && (
            <div className="tw-mb-4 tw-flex tw-items-center tw-gap-1">
              <Typography variant="caption-metadata" className="tw-text-ink-secondary tw-shrink-0">
                Attribute:
              </Typography>
              <Typography variant="body-primary" className="tw-text-ink tw-truncate">
                {attributeName}
              </Typography>
            </div>
          )
        )}
        {renderValueInput()}
        {renderValuesList()}
      </div>
    </ModalWrapper>
  );
};

export default CreateAttributeValueModal;
